import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Plus, Search, Loader2, KeyRound, Eye, EyeOff, Copy,
  MoreHorizontal, Pencil, Trash2, Globe, User, Paperclip, Upload, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = { title: '', username: '', password: '', url: '', notes: '', company_id: '' };

export default function PasswordsPage() {
  const { companies, currentCompany } = useCompany();
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [visibleIds, setVisibleIds] = useState(new Set());
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/passwords/export/csv',
    importUrl: '/api/passwords/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchPasswords(),
    entityName: 'passwords'
  });

  const isPersonal = currentCompany?.slug === 'stuart';

  const fetchPasswords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (isPersonal) params.company_id = currentCompany.id;
      const res = await axios.get(`${API_URL}/api/passwords`, { params, withCredentials: true });
      setPasswords(res.data);
    } catch {
      toast.error('Failed to load passwords');
    } finally {
      setLoading(false);
    }
  }, [search, isPersonal, currentCompany]);

  useEffect(() => { fetchPasswords(); }, [fetchPasswords]);

  const getCompanyName = (id) => companies.find(c => c.id === id)?.name || 'Unknown';
  const getCompanyColor = (id) => companies.find(c => c.id === id)?.color || '#6366F1';

  const filteredPasswords = passwords.filter(pw => {
    if (isPersonal) return true; // Already filtered by API
    // In business view, exclude Stuart passwords
    const stuartCompany = companies.find(c => c.slug === 'stuart');
    if (stuartCompany && pw.company_id === stuartCompany.id) return false;
    if (companyFilter !== 'all' && pw.company_id !== companyFilter) return false;
    return true;
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    const companyId = formData.company_id;
    if (!companyId) { toast.error('Select a company first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/files/upload?company_id=${companyId}`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFile(res.data);
      toast.success('File attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAdd = async () => {
    if (!formData.title.trim()) { toast.error('Title is required'); return; }
    if (!formData.password.trim()) { toast.error('Password is required'); return; }
    if (!formData.company_id) { toast.error('Select a company'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/passwords`, {
        company_id: formData.company_id,
        title: formData.title,
        username: formData.username,
        password: formData.password,
        url: formData.url,
        notes: formData.notes,
        file_id: uploadedFile?.id || null
      }, { withCredentials: true });
      toast.success('Password saved');
      setIsAddOpen(false);
      setFormData(EMPTY_FORM);
      setUploadedFile(null);
      fetchPasswords();
    } catch { toast.error('Failed to save password'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!formData.title.trim()) { toast.error('Title is required'); return; }
    if (!formData.password.trim()) { toast.error('Password is required'); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/passwords/${selected.id}`, {
        title: formData.title,
        username: formData.username,
        password: formData.password,
        url: formData.url,
        notes: formData.notes,
        file_id: uploadedFile?.id || selected.file_id || null
      }, { withCredentials: true });
      toast.success('Password updated');
      setIsEditOpen(false);
      setSelected(null);
      setFormData(EMPTY_FORM);
      setUploadedFile(null);
      fetchPasswords();
    } catch { toast.error('Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/passwords/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Password deleted');
      setDeleteTarget(null);
      fetchPasswords();
    } catch { toast.error('Failed to delete'); }
  };

  const openAdd = () => {
    setFormData({ ...EMPTY_FORM, company_id: currentCompany?.id || '' });
    setUploadedFile(null);
    setIsAddOpen(true);
  };

  const openEdit = (pw) => {
    setSelected(pw);
    setFormData({ title: pw.title, username: pw.username || '', password: pw.password, url: pw.url || '', notes: pw.notes || '', company_id: pw.company_id });
    setUploadedFile(pw.file_info || null);
    setIsEditOpen(true);
  };

  const toggleVisible = (id) => {
    setVisibleIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const PasswordForm = ({ showCompanyPicker }) => (
    <div className="space-y-4 py-2">
      {showCompanyPicker && (
        <div className="space-y-2">
          <Label>Company *</Label>
          <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })}>
            <SelectTrigger data-testid="password-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
            <SelectContent>{companies.filter(c => !c.is_personal).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label>Title *</Label>
        <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. AWS Console" data-testid="password-title-input" />
      </div>
      <div className="space-y-2">
        <Label>Username</Label>
        <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="Username or email" data-testid="password-username-input" />
      </div>
      <div className="space-y-2">
        <Label>Password *</Label>
        <Input type="text" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Password" data-testid="password-password-input" />
      </div>
      <div className="space-y-2">
        <Label>URL</Label>
        <Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://..." data-testid="password-url-input" />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." data-testid="password-notes-input" />
      </div>
      {/* File Attachment */}
      <div className="space-y-2">
        <Label>Attachment</Label>
        {uploadedFile ? (
          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
            <Paperclip className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700 flex-1 truncate">{uploadedFile.filename}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setUploadedFile(null)}><X className="w-3.5 h-3.5" /></Button>
          </div>
        ) : (
          <div>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="password-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading || !formData.company_id} data-testid="password-attach-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {uploading ? 'Uploading...' : 'Attach File'}
            </Button>
            {!formData.company_id && <p className="text-xs text-gray-400 mt-1">Select a company first</p>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="passwords-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Passwords</h1>
          <p className="text-gray-500 mt-1">{isPersonal ? 'Your personal password vault' : 'Securely store and manage credentials across all companies'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="passwords" />
          <Button onClick={openAdd} data-testid="add-password-btn"><Plus className="w-4 h-4 mr-2" />Add Password</Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search passwords..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-passwords" />
        </div>
        {!isPersonal && (
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="password-company-filter"><SelectValue placeholder="All Companies" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.filter(c => !c.is_personal).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filteredPasswords.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <KeyRound className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{search || companyFilter !== 'all' ? 'No passwords match your filters' : 'No passwords yet'}</h3>
          <p className="text-gray-500 mb-4">Store your first credential to get started</p>
          {!search && companyFilter === 'all' && <Button onClick={openAdd}><Plus className="w-4 h-4 mr-2" />Add Password</Button>}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                {!isPersonal && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Password</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPasswords.map((pw) => (
                <tr key={pw.id} className="hover:bg-gray-50" data-testid={`password-row-${pw.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-gray-400" /><span className="font-medium text-gray-900">{pw.title}</span></div>
                  </td>
                  {!isPersonal && (
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${getCompanyColor(pw.company_id)}15`, color: getCompanyColor(pw.company_id) }}>
                      {getCompanyName(pw.company_id)}
                    </span>
                  </td>
                  )}
                  <td className="px-4 py-3">
                    {pw.username ? (
                      <button onClick={() => copyToClipboard(pw.username, 'Username')} className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 group" data-testid={`copy-username-${pw.id}`}>
                        <User className="w-3.5 h-3.5" /><span className="text-sm">{pw.username}</span><Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 text-gray-400" />
                      </button>
                    ) : <span className="text-gray-400 text-sm">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-600">{visibleIds.has(pw.id) ? pw.password : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}</span>
                      <button onClick={() => toggleVisible(pw.id)} className="text-gray-400 hover:text-gray-600" data-testid={`toggle-password-${pw.id}`}>
                        {visibleIds.has(pw.id) ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => copyToClipboard(pw.password, 'Password')} className="text-gray-400 hover:text-gray-600" data-testid={`copy-password-${pw.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {pw.url ? (
                      <a href={pw.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm truncate max-w-[200px]">
                        <Globe className="w-3.5 h-3.5 flex-shrink-0" />{pw.url.replace(/^https?:\/\//, '')}
                      </a>
                    ) : <span className="text-gray-400 text-sm">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {pw.file_info ? (
                      <a href={`${API_URL}/api/files/${pw.file_id}/download`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm">
                        <Paperclip className="w-3.5 h-3.5" /><span className="truncate max-w-[80px]">{pw.file_info.filename}</span>
                      </a>
                    ) : <span className="text-gray-400 text-sm">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(pw)} data-testid={`edit-password-${pw.id}`}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteTarget(pw)} className="text-red-600" data-testid={`delete-password-${pw.id}`}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent data-testid="add-password-dialog">
          <DialogHeader><DialogTitle>Add Password</DialogTitle><DialogDescription>Store a new credential with optional file attachment.</DialogDescription></DialogHeader>
          <PasswordForm showCompanyPicker={!isPersonal} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={submitting} data-testid="save-password-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent data-testid="edit-password-dialog">
          <DialogHeader><DialogTitle>Edit Password</DialogTitle><DialogDescription>Update this credential.</DialogDescription></DialogHeader>
          <PasswordForm showCompanyPicker={false} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Password</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete "{deleteTarget?.title}"?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
